import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import TestRequestFormWithCountries from "@/components/TestRequestFormWithCountries";
import TelegramButton from "@/components/TelegramButton";
import { useIsMobile } from "@/hooks/use-mobile";

const offers = [
  {
    name: "TEST",
    duration: "24H - ESSAI",
    price: "GRATUIT",
    pricePerMonth: null,
    popular: false,
    isTest: true,
    cta: "ESSAYEZ GRATUITEMENT",
    features: [
      "+15 000 CHAÎNES TV",
      "+100 000 FILMS",
      "+25 000 SÉRIES",
      "FONCTIONNE DANS LE MONDE ENTIER",
      "MISE À JOUR AUTO DU CONTENU",
      "SUPPORT 7J/7",
    ],
  },
  {
    name: "3 MOIS",
    duration: "90 JOURS",
    price: "25",
    pricePerMonth: "8,33",
    popular: false,
    stripeProductType: "3months" as const,
    cta: "COMMANDER",
    features: [
      "+15 000 CHAÎNES TV",
      "+100 000 FILMS",
      "+25 000 SÉRIES",
      "FONCTIONNE DANS LE MONDE ENTIER",
      "MISE À JOUR AUTO DU CONTENU",
      "SUPPORT 7J/7",
    ],
  },
  {
    name: "6 MOIS",
    duration: "180 JOURS",
    price: "40",
    pricePerMonth: "6,67",
    popular: false,
    stripeProductType: "6months" as const,
    cta: "COMMANDER",
    features: [
      "+15 000 CHAÎNES TV",
      "+100 000 FILMS",
      "+25 000 SÉRIES",
      "FONCTIONNE DANS LE MONDE ENTIER",
      "MISE À JOUR AUTO DU CONTENU",
      "SUPPORT 7J/7",
    ],
  },
  {
    name: "12 MOIS",
    duration: "365 JOURS",
    price: "60",
    pricePerMonth: "5",
    popular: true,
    stripeProductType: "12months" as const,
    cta: "MEILLEURE OFFRE",
    features: [
      "+15 000 CHAÎNES TV",
      "+100 000 FILMS",
      "+25 000 SÉRIES",
      "FONCTIONNE DANS LE MONDE ENTIER",
      "MISE À JOUR AUTO DU CONTENU",
      "SUPPORT VIP 24/7",
    ],
  },
  {
    name: "MENSUEL",
    duration: "ACCÈS MENSUEL",
    price: "9,99",
    pricePerMonth: "9,99",
    popular: false,
    isMonthly: true,
    stripeProductType: "monthly" as const,
    cta: "OBTENIR MON ACCÈS",
    features: [
      "+15 000 CHAÎNES TV",
      "+100 000 FILMS",
      "+25 000 SÉRIES",
      "FONCTIONNE DANS LE MONDE ENTIER",
      "MISE À JOUR AUTO DU CONTENU",
      "SUPPORT 7J/7",
    ],
  },
];

const OffersBlock = () => {
  const isMobile = useIsMobile();
  const mainOffers = offers.filter((o) => !o.isMonthly);
  const monthlyOffer = offers.find((o) => o.isMonthly);

  const mobileOrder = isMobile
    ? [...mainOffers].reverse().reduce((acc, offer, i) => {
        acc.push(offer);
        if (i === 2 && monthlyOffer) acc.push(monthlyOffer);
        return acc;
      }, [] as typeof offers)
    : mainOffers;

  const renderCard = (offer: (typeof offers)[0], index: number) => (
    <Card
      key={`${offer.name}-${index}`}
      className={`relative overflow-hidden transition-all duration-300 hover:scale-105 rounded-xl border-2 flex flex-col ${
        offer.popular
          ? "bg-gradient-to-b from-primary to-primary/80 border-primary glow-primary"
          : "bg-card border-primary/40 hover:border-primary/80"
      }`}
    >
      <CardContent className="p-6 text-center flex flex-col flex-1">
        <h3
          className={`text-2xl font-bold mb-2 tracking-wider ${
            offer.popular ? "text-primary-foreground" : "text-primary"
          }`}
        >
          {offer.name}
        </h3>
        <p
          className={`text-sm mb-4 tracking-wide ${
            offer.popular ? "text-primary-foreground/80" : "text-muted-foreground"
          }`}
        >
          {offer.duration}
        </p>

        <div className="mb-4 min-h-[80px]">
          <div
            className={`text-5xl font-black ${
              offer.popular ? "text-primary-foreground" : "text-foreground"
            }`}
          >
            {offer.isTest ? offer.price : `${offer.price} €`}
          </div>
          {offer.pricePerMonth ? (
            <p
              className={`text-sm mt-1 ${
                offer.popular ? "text-primary-foreground/70" : "text-muted-foreground"
              }`}
            >
              soit {offer.pricePerMonth} €/mois
            </p>
          ) : (
            <p className="text-sm mt-1 invisible">-</p>
          )}
        </div>

        {offer.isTest ? (
          <div className="w-full mb-6">
            <TestRequestFormWithCountries
              trigger={
                <Button className="w-full font-bold py-5 text-sm rounded-full transition-all duration-300 group btn-primary text-primary-foreground">
                  {offer.cta}
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              }
            />
          </div>
        ) : (
          <div className="w-full mb-6">
            <TelegramButton
              className={`w-full font-bold py-5 text-sm rounded-full transition-all duration-300 group ${
                offer.popular
                  ? "bg-background text-foreground hover:bg-background/90"
                  : "btn-primary text-primary-foreground"
              }`}
            >
              {offer.cta}
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </TelegramButton>
          </div>
        )}

        <ul className="space-y-3 text-left mb-4 flex-1">
          {offer.features.map((feature, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-secondary flex-shrink-0">✅</span>
              <span
                className={`text-sm ${
                  offer.popular ? "text-primary-foreground/90" : "text-foreground/80"
                }`}
              >
                {feature}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );

  return (
    <section className="py-14 md:py-20">
      <h2 className="text-3xl md:text-4xl font-bold text-center mb-3 text-foreground">
        NOS OFFRES D'ABONNEMENT IPTV
      </h2>
      <p className="text-center text-muted-foreground mb-10 text-base">
        Profitez de +15 000 chaînes, +100 000 films et +25 000 séries en streaming HD & 4K.
      </p>

      {isMobile ? (
        <div className="grid grid-cols-1 gap-4">
          {mobileOrder.map((offer, index) => renderCard(offer, index))}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-4">
            {mainOffers.map((offer, index) => renderCard(offer, index))}
          </div>
          {monthlyOffer && (
            <Card className="relative overflow-hidden transition-all duration-300 hover:scale-[1.02] rounded-xl border-2 bg-card border-primary/40 hover:border-primary/80">
              <CardContent className="p-6 lg:p-8">
                <div className="flex items-center gap-6 lg:gap-8">
                  <div className="text-center shrink-0">
                    <h3 className="text-xl lg:text-2xl font-bold tracking-wider text-primary">
                      {monthlyOffer.name}
                    </h3>
                    <p className="text-xs lg:text-sm tracking-wide text-muted-foreground mt-1">
                      {monthlyOffer.duration}
                    </p>
                  </div>
                  <div className="text-center shrink-0">
                    <div className="text-3xl lg:text-5xl font-black text-foreground">
                      {monthlyOffer.price} €
                    </div>
                    <p className="text-xs lg:text-sm mt-1 text-muted-foreground">
                      soit {monthlyOffer.pricePerMonth} €/mois
                    </p>
                  </div>
                  <ul className="flex-1 grid grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-1.5">
                    {monthlyOffer.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-1.5">
                        <span className="text-secondary flex-shrink-0 text-sm">✅</span>
                        <span className="text-xs lg:text-sm text-foreground/80">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="shrink-0 w-[180px] lg:w-[220px]">
                    <TelegramButton
                      className="w-full font-bold py-4 lg:py-5 text-xs lg:text-sm rounded-full transition-all duration-300 group btn-primary text-primary-foreground"
                    >
                      {monthlyOffer.cta}
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </TelegramButton>
                  </div>
                </div>
                <p className="text-center text-sm text-primary font-semibold mt-4">
                  💰 Économisez plus de 800 € par an par rapport aux plateformes classiques !
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <p className="text-center text-sm text-muted-foreground mt-6">
        🔒 Paiement sécurisé par Stripe •{" "}
        <Link to="/nos-offres" className="text-primary underline hover:no-underline">
          Voir toutes nos offres en détail
        </Link>
      </p>
    </section>
  );
};

export default OffersBlock;
